/**
 * Pointer handling for the two maps.
 *
 * Every gesture here ends in an `issue(...)` call. Nothing in this file reads or
 * writes simulation state directly — it converts pixels to coordinates and
 * emits commands, which is the whole of spec §11's UI contract.
 */

import type { Command } from '../sim/commands';
import { plotAt } from '../sim/city';
import type { BuildingId } from '../sim/data';
import type { GameState, HexCoord, PlotCoord } from '../sim/types';
import { cityMapLayout, plotAtPixel } from './canvas/cityMap';
import { hexAtPixel, worldMapLayout } from './canvas/worldMap';

export interface Selection {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface MapInteraction {
  selectedHex(): HexCoord | null;
  citySelection(): Selection | null;
  /** True while a drag is in progress, so the caller can repaint the preview. */
  dragging(): boolean;
}

export interface InteractionOptions {
  worldCanvas: HTMLElement;
  cityCanvas: HTMLElement;
  getState: () => GameState;
  activeBuilding: () => BuildingId;
  issue: (command: Command) => void;
  onChange: () => void;
}

function localPoint(target: HTMLElement, event: MouseEvent): { x: number; y: number } {
  const rect = target.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

export function setupInteraction(options: InteractionOptions): MapInteraction {
  const { worldCanvas, cityCanvas, getState, activeBuilding, issue, onChange } = options;

  let selectedHex: HexCoord | null = null;
  let anchor: PlotCoord | null = null;
  let cursor: PlotCoord | null = null;

  // --- world map: click to select a hex ---
  worldCanvas.addEventListener('click', (event) => {
    const point = localPoint(worldCanvas, event);
    const rect = worldCanvas.getBoundingClientRect();
    const layout = worldMapLayout(rect.width, rect.height);
    selectedHex = hexAtPixel(layout, getState(), point.x, point.y);
    onChange();
  });

  // --- city map: drag a rectangle to queue, right-click to demolish ---
  function plotFromEvent(event: MouseEvent): PlotCoord | null {
    const rect = cityCanvas.getBoundingClientRect();
    const layout = cityMapLayout(rect.width, rect.height);
    const point = localPoint(cityCanvas, event);
    return plotAtPixel(layout, point.x, point.y);
  }

  cityCanvas.addEventListener('mousedown', (event) => {
    if (event.button !== 0) return;
    anchor = plotFromEvent(event);
    cursor = anchor;
    onChange();
  });

  cityCanvas.addEventListener('mousemove', (event) => {
    if (anchor === null) return;
    const plot = plotFromEvent(event);
    if (plot !== null) cursor = plot;
    onChange();
  });

  function finishDrag(): void {
    if (anchor === null || cursor === null) {
      anchor = null;
      cursor = null;
      return;
    }

    const x0 = Math.min(anchor.x, cursor.x);
    const x1 = Math.max(anchor.x, cursor.x);
    const y0 = Math.min(anchor.y, cursor.y);
    const y1 = Math.max(anchor.y, cursor.y);

    const plots: PlotCoord[] = [];
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) plots.push({ x, y });
    }

    anchor = null;
    cursor = null;

    if (plots.length > 0) {
      issue({ type: 'queueBuild', plots, building: activeBuilding() });
    }
    onChange();
  }

  cityCanvas.addEventListener('mouseup', (event) => {
    if (event.button !== 0) return;
    finishDrag();
  });

  // A drag that leaves the canvas still commits, rather than being silently
  // abandoned with the preview rectangle stuck on screen.
  cityCanvas.addEventListener('mouseleave', () => {
    if (anchor !== null) finishDrag();
  });

  cityCanvas.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    const plot = plotFromEvent(event);
    if (plot === null) return;

    const existing = plotAt(getState(), plot);
    if (existing === null) return;

    if (existing.state === 'complete') {
      // Toggling is now the common action, since buildings finish switched off.
      // Demolition is the rarer and irreversible one, so it takes the modifier.
      if (event.shiftKey) issue({ type: 'demolish', plot });
      else issue({ type: 'setBuildingEnabled', plot, enabled: !existing.enabled });
    } else if (existing.state === 'queued') {
      // Cancelling by index means finding it; the queue is the authority on
      // order, so look it up rather than assuming row-major position.
      const index = getState().buildQueue.findIndex(
        (item) => item.plot.x === plot.x && item.plot.y === plot.y,
      );
      if (index >= 0) issue({ type: 'cancelQueued', index });
    }
    onChange();
  });

  return {
    selectedHex: () => selectedHex,
    citySelection: () =>
      anchor === null || cursor === null
        ? null
        : { x0: anchor.x, y0: anchor.y, x1: cursor.x, y1: cursor.y },
    dragging: () => anchor !== null,
  };
}
